/**
 * PrimitivesLayer — renders circles, arcs, splines, polylines and their hatches.
 *
 * Sits in the SVG canvas between WallLayer and AnnotationLayer.
 * All coordinates use the same world→screen transform as WallLayer.
 */
import React from "react";
import { w2p, izoom } from "../../utils/coords";
import { splineToPath, HATCH_PATTERNS } from "../../engine/primitives";

// ─── Hatch pattern SVG defs ───────────────────────────────────────────────────
function HatchDefs({ zoom }) {
  const s = izoom(zoom);
  return (
    <defs>
      {Object.values(HATCH_PATTERNS).filter(p => p.id !== "none").map(pat => {
        const spacing = pat.spacing * s * 4;
        const angle   = pat.angle || 0;
        const patId   = `hatch-${pat.id}`;
        if (pat.type === "brick") {
          return (
            <pattern key={patId} id={patId} width={spacing*2} height={spacing}
              patternUnits="userSpaceOnUse" patternTransform={`rotate(${angle})`}>
              <rect width={spacing*2} height={spacing} fill="none" stroke="currentColor" strokeWidth={s*0.4}/>
              <line x1={spacing} y1={0} x2={spacing} y2={spacing/2} stroke="currentColor" strokeWidth={s*0.4}/>
              <line x1={0} y1={spacing/2} x2={spacing*2} y2={spacing/2} stroke="currentColor" strokeWidth={s*0.4}/>
            </pattern>
          );
        }
        if (pat.type === "zigzag") {
          const w = spacing * 2;
          return (
            <pattern key={patId} id={patId} width={w} height={spacing}
              patternUnits="userSpaceOnUse">
              <polyline points={`0,${spacing/2} ${w/4},0 ${w/2},${spacing/2} ${w*3/4},${spacing} ${w},${spacing/2}`}
                fill="none" stroke="currentColor" strokeWidth={s*0.5}/>
            </pattern>
          );
        }
        if (pat.dots) {
          return (
            <pattern key={patId} id={patId} width={spacing} height={spacing}
              patternUnits="userSpaceOnUse" patternTransform={`rotate(${angle})`}>
              <line x1={0} y1={0} x2={spacing} y2={spacing} stroke="currentColor" strokeWidth={s*0.4}/>
              <circle cx={spacing/2} cy={spacing/2} r={s*0.8} fill="currentColor"/>
            </pattern>
          );
        }
        // default: lines
        return (
          <pattern key={patId} id={patId} width={spacing} height={spacing}
            patternUnits="userSpaceOnUse" patternTransform={`rotate(${angle})`}>
            <line x1={0} y1={0} x2={0} y2={spacing} stroke="currentColor" strokeWidth={s*0.5}/>
            {pat.double && <line x1={spacing*0.5} y1={0} x2={spacing*0.5} y2={spacing} stroke="currentColor" strokeWidth={s*0.3}/>}
          </pattern>
        );
      })}
    </defs>
  );
}

// ─── Individual primitive renderers ──────────────────────────────────────────

function CircleEl({ c, isSelected, zoom, onSelect, hatch }) {
  const s   = izoom(zoom);
  const cx  = w2p(c.cx), cy = w2p(c.cy), r = w2p(c.r);
  const col = isSelected ? "#e8a838" : (c.color || "#3b82f6");
  const lw  = (c.lineweight || 1) * s * 0.8;
  return (
    <g className="EH" style={{ cursor: "pointer" }}
      onClick={ev => { ev.stopPropagation(); onSelect?.("circle", c.id); }}>
      <circle cx={cx} cy={cy} r={r} fill="transparent" stroke="transparent" strokeWidth={s*16}/>
      {hatch && hatch.patternId !== "none" && (
        <circle cx={cx} cy={cy} r={r}
          fill={`url(#hatch-${hatch.patternId})`}
          stroke="none" style={{ color: col, opacity: 0.4 }}/>
      )}
      <circle cx={cx} cy={cy} r={r}
        fill="none"
        stroke={col}
        strokeWidth={isSelected ? lw * 2 : lw}
        strokeDasharray={c.linetype === "dashed" ? `${s*8} ${s*4}` : c.linetype === "dotted" ? `${s*2} ${s*4}` : undefined}
      />
      {isSelected && <circle cx={cx} cy={cy} r={s*4} fill="#e8a838"/>}
    </g>
  );
}

function ArcEl({ a, isSelected, zoom, onSelect }) {
  const s  = izoom(zoom);
  const cx = w2p(a.cx), cy = w2p(a.cy), r = w2p(a.r);
  const col = isSelected ? "#e8a838" : (a.color || "#3b82f6");
  const lw  = (a.lineweight || 1) * s * 0.8;
  const x1  = cx + r * Math.cos(a.startAngle);
  const y1  = cy + r * Math.sin(a.startAngle);
  const x2  = cx + r * Math.cos(a.endAngle);
  const y2  = cy + r * Math.sin(a.endAngle);
  const da  = a.endAngle - a.startAngle;
  const largeArc = (da > Math.PI || da < -Math.PI) ? 1 : 0;
  const sweep = da > 0 ? 1 : 0;
  return (
    <g className="EH" style={{ cursor: "pointer" }}
      onClick={ev => { ev.stopPropagation(); onSelect?.("arc", a.id); }}>
      <path d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} ${sweep} ${x2} ${y2}`}
        fill="none" stroke="transparent" strokeWidth={s*16}/>
      <path d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} ${sweep} ${x2} ${y2}`}
        fill="none" stroke={col}
        strokeWidth={isSelected ? lw * 2 : lw}/>
      {isSelected && <>
        <circle cx={x1} cy={y1} r={s*4} fill="#e8a838"/>
        <circle cx={x2} cy={y2} r={s*4} fill="#e8a838"/>
      </>}
    </g>
  );
}

function SplineEl({ sp, isSelected, zoom, onSelect }) {
  const s    = izoom(zoom);
  const col  = isSelected ? "#e8a838" : (sp.color || "#3b82f6");
  const lw   = (sp.lineweight || 1) * s * 0.8;
  // Convert mm pts to screen pts
  const screenPts = sp.pts.map(p => ({ x: w2p(p.x), y: w2p(p.y) }));
  const d    = splineToPath(screenPts, sp.closed);
  if (!d) return null;
  return (
    <g className="EH" style={{ cursor: "pointer" }}
      onClick={ev => { ev.stopPropagation(); onSelect?.("spline", sp.id); }}>
      <path d={d} fill="none" stroke="transparent" strokeWidth={s*14}/>
      <path d={d} fill={sp.filled && sp.closed ? col + "20" : "none"}
        stroke={col} strokeWidth={isSelected ? lw * 2 : lw}/>
      {isSelected && screenPts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={s*4} fill="#e8a838"/>
      ))}
    </g>
  );
}

function PolylineEl({ pl, isSelected, zoom, onSelect, hatch }) {
  const s   = izoom(zoom);
  const col = isSelected ? "#e8a838" : (pl.color || "#3b82f6");
  const lw  = (pl.lineweight || 1) * s * 0.8;
  const pts = pl.pts.map(p => `${w2p(p.x)},${w2p(p.y)}`).join(" ");
  return (
    <g className="EH" style={{ cursor: "pointer" }}
      onClick={ev => { ev.stopPropagation(); onSelect?.("polyline", pl.id); }}>
      {pl.closed ? (
        <polygon points={pts}
          fill={hatch && hatch.patternId !== "none"
            ? `url(#hatch-${hatch.patternId})`
            : (pl.filled ? col + "22" : "none")}
          stroke={col} strokeWidth={isSelected ? lw * 2 : lw}
          style={{ color: col }}/>
      ) : (
        <polyline points={pts} fill="none" stroke={col} strokeWidth={isSelected ? lw * 2 : lw}/>
      )}
    </g>
  );
}

// ─── Main layer ───────────────────────────────────────────────────────────────

export default function PrimitivesLayer({ st, selId, zoom, onSelect }) {
  const circles   = Object.values(st.circles   || {});
  const arcs      = Object.values(st.arcs      || {});
  const splines   = Object.values(st.splines   || {});
  const polylines = Object.values(st.polylines || {});

  const hasAny = circles.length || arcs.length || splines.length || polylines.length;
  if (!hasAny) return null;

  return (
    <g>
      <HatchDefs zoom={zoom}/>
      {circles.map(c => (
        <CircleEl key={c.id} c={c} zoom={zoom}
          isSelected={selId?.id === c.id && selId?.type === "circle"}
          onSelect={onSelect}
          hatch={(st.circleHatches || {})[c.id]}/>
      ))}
      {arcs.map(a => (
        <ArcEl key={a.id} a={a} zoom={zoom}
          isSelected={selId?.id === a.id && selId?.type === "arc"}
          onSelect={onSelect}/>
      ))}
      {splines.map(sp => (
        <SplineEl key={sp.id} sp={sp} zoom={zoom}
          isSelected={selId?.id === sp.id && selId?.type === "spline"}
          onSelect={onSelect}/>
      ))}
      {polylines.map(pl => (
        <PolylineEl key={pl.id} pl={pl} zoom={zoom}
          isSelected={selId?.id === pl.id && selId?.type === "polyline"}
          onSelect={onSelect}
          hatch={(st.polylineHatches || {})[pl.id]}/>
      ))}
    </g>
  );
}
